'use client'

import { memo, useMemo, useRef, useEffect } from 'react'

import { useReducedMotion } from 'framer-motion'

import type { GameLetter, GamePosition } from '../types'

import { Square } from './Square'

interface BoardProps {
  board: GameLetter[][]
  activePositions: GamePosition[]
  foundPositions: GamePosition[]
  onSquareClick: (position: GamePosition) => void
  onSquareEnter: (position: GamePosition) => void
  /** CHAMADO quando o usuário termina a seleção (mouse up / touch end / cancel) */
  onSelectionEnd: () => void
  /** Indica se a seleção atual contém erro (para mostrar conexões vermelhas) */
  selectionError?: boolean
  /** Indica se a seleção atual é uma palavra válida (para mostrar conexões verdes) */
  selectionSuccess?: boolean
}

const keyPos = (r: number, c: number) => `${r}:${c}`

/** Deslocamentos em px (centro primeiro) para hit slop tátil próximo aos bicos das células. */
const POSITION_HIT_SLOP_OFFSETS = [
  [0, 0],
  [-11, 0],
  [11, 0],
  [0, -11],
  [0, 11],
  [-8, -8],
  [-8, 8],
  [8, -8],
  [8, 8]
] as const

function readSquareAttrs(el: HTMLElement): GamePosition | null {
  const row = Number(el.getAttribute('data-square-row'))
  const column = Number(el.getAttribute('data-square-col'))
  if (Number.isNaN(row) || Number.isNaN(column)) return null
  return { row, column }
}

// Função de comparação personalizada para o memo
function arePropsEqual(prevProps: BoardProps, nextProps: BoardProps) {
  // Verificar se selectionError mudou (crítico para feedback visual)
  if (prevProps.selectionError !== nextProps.selectionError) {
    return false
  }

  // Verificar se selectionSuccess mudou (crítico para feedback visual)
  if (prevProps.selectionSuccess !== nextProps.selectionSuccess) {
    return false
  }

  // Verificar se activePositions mudou
  if (prevProps.activePositions.length !== nextProps.activePositions.length) {
    return false
  }

  // Verificar se o conteúdo das activePositions mudou
  for (let i = 0; i < prevProps.activePositions.length; i++) {
    const prev = prevProps.activePositions[i]
    const next = nextProps.activePositions[i]
    if (prev.row !== next.row || prev.column !== next.column) {
      return false
    }
  }

  // Verificar se foundPositions mudou
  if (prevProps.foundPositions.length !== nextProps.foundPositions.length) {
    return false
  }

  // Verificar se o conteúdo das foundPositions mudou
  for (let i = 0; i < prevProps.foundPositions.length; i++) {
    const prev = prevProps.foundPositions[i]
    const next = nextProps.foundPositions[i]
    if (prev.row !== next.row || prev.column !== next.column) {
      return false
    }
  }

  return true
}

export const Board = memo<BoardProps>(function Board({
  board,
  activePositions,
  foundPositions,
  onSquareClick,
  onSquareEnter,
  onSelectionEnd,
  selectionError = false,
  selectionSuccess = false
}) {
  const rows = board.length
  const cols = rows ? board[0].length : 0
  const prefersReducedMotion = useReducedMotion()

  // ---- Refs para overlay/gesture ----
  const overlayRef = useRef<HTMLDivElement>(null)
  const rafRef = useRef<number | null>(null)
  const draggingRef = useRef(false)
  const lastSentRef = useRef<string | null>(null)
  const pendingPointRef = useRef<{ x: number; y: number } | null>(null)

  // ---- Sets auxiliares p/ estados (se precisar no futuro) ----
  const activeSet = useMemo(() => {
    const s = new Set<string>()
    for (const p of activePositions) s.add(keyPos(p.row, p.column))
    return s
  }, [activePositions])
  const foundSet = useMemo(() => {
    const s = new Set<string>()
    for (const p of foundPositions) s.add(keyPos(p.row, p.column))
    return s
  }, [foundPositions])

  const isActive = (r: number, c: number) => activeSet.has(keyPos(r, c))
  const isFound = (r: number, c: number) =>
    !isActive(r, c) && foundSet.has(keyPos(r, c))
  const isCompleted = (r: number, c: number) => foundSet.has(keyPos(r, c))

  // =========================
  //   SVG helpers (GL0W)
  // =========================
  // ViewBox baseado em "células": cada célula tem 8 unidades
  const cellSize = 9
  const viewW = cols * cellSize
  const viewH = rows * cellSize
  const strokeW = cellSize * 0.9 // faixa grossa para parecer bloco

  const getPositionCoords = (pos: { row: number; column: number }) => ({
    x: pos.column * cellSize + cellSize / 2,
    y: pos.row * cellSize + cellSize / 2
  })

  const glowBlurDeviation = prefersReducedMotion ? cellSize * 0.04 : cellSize * 0.12
  const outlineStroke = selectionError
    ? '#7f1d1d'
    : selectionSuccess
      ? '#14532d'
      : '#0f172a'

  const generateConnectionPath = (positions: GamePosition[]) => {
    if (positions.length < 2) return ''
    let d = ''
    for (let i = 0; i < positions.length - 1; i++) {
      const a = getPositionCoords(positions[i])
      const b = getPositionCoords(positions[i + 1])
      if (i === 0) d += `M ${a.x} ${a.y}`
      d += ` L ${b.x} ${b.y}`
    }
    return d
  }

  // =========================
  //   Pointer Events (drag)
  // =========================
  function posFromClient(x: number, y: number): GamePosition | null {
    const tryHit = (px: number, py: number): GamePosition | null => {
      const els = document.elementsFromPoint(px, py)
      const sq = els.find(
        (el): el is HTMLElement =>
          el instanceof HTMLElement &&
          el.hasAttribute('data-square-row') &&
          el.hasAttribute('data-square-col')
      )
      if (!sq) return null
      return readSquareAttrs(sq)
    }

    for (const [dx, dy] of POSITION_HIT_SLOP_OFFSETS) {
      const hit = tryHit(x + dx, y + dy)
      if (hit) return hit
    }
    return null
  }

  const flushMove = () => {
    rafRef.current = null
    const p = pendingPointRef.current
    if (!p) return
    pendingPointRef.current = null
    const pos = posFromClient(p.x, p.y)
    if (!pos) return
    const k = keyPos(pos.row, pos.column)
    if (k !== lastSentRef.current) {
      lastSentRef.current = k
      onSquareEnter(pos)
    }
  }

  const finalizeSelection = (e?: PointerEvent) => {
    draggingRef.current = false
    lastSentRef.current = null
    pendingPointRef.current = null
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }

    // 1) avisa o app
    onSelectionEnd()

    // 2) compat: dispara um mouseup global (se alguém ainda escuta document/window)
    try {
      const ev = new MouseEvent('mouseup', { bubbles: true, cancelable: true })
      document.dispatchEvent(ev)
      window.dispatchEvent(ev)
    } catch {}
    // 3) libera pointer capture (se existir)
    try {
      const target = overlayRef.current
      if (e && target) target.releasePointerCapture?.(e.pointerId)
    } catch {}
  }

  useEffect(() => {
    const layer = overlayRef.current
    if (!layer) return

    const onDown = (e: PointerEvent) => {
      e.preventDefault()
      draggingRef.current = true
      layer.setPointerCapture?.(e.pointerId)
      const pos = posFromClient(e.clientX, e.clientY)
      if (pos) {
        lastSentRef.current = keyPos(pos.row, pos.column)
        onSquareClick(pos)
      }
    }

    const onMove = (e: PointerEvent) => {
      if (!draggingRef.current) return
      e.preventDefault()
      pendingPointRef.current = { x: e.clientX, y: e.clientY }
      if (rafRef.current == null)
        rafRef.current = requestAnimationFrame(flushMove)
    }

    const onUp = (e: PointerEvent) => {
      if (!draggingRef.current) return
      e.preventDefault()
      finalizeSelection(e)
    }

    const onCancel = (e: PointerEvent) => {
      if (!draggingRef.current) return
      e.preventDefault()
      finalizeSelection(e)
    }

    layer.addEventListener('pointerdown', onDown, { passive: false })
    layer.addEventListener('pointermove', onMove, { passive: false })
    layer.addEventListener('pointerup', onUp, { passive: false })
    layer.addEventListener('pointercancel', onCancel, { passive: false })

    // ESC cancela seleção no desktop
    const onKey = (ke: KeyboardEvent) => {
      if (ke.key === 'Escape' && draggingRef.current) {
        ke.preventDefault()
        finalizeSelection()
      }
    }
    window.addEventListener('keydown', onKey)

    return () => {
      layer.removeEventListener('pointerdown', onDown)
      layer.removeEventListener('pointermove', onMove)
      layer.removeEventListener('pointerup', onUp)
      layer.removeEventListener('pointercancel', onCancel)
      window.removeEventListener('keydown', onKey)
    }
  }, [onSquareClick, onSquareEnter, onSelectionEnd])

  return (
    <div className="w-full h-0 pb-[100%] relative">
      {/* wrapper compartilhado para alinhar SVG e GRID (padding igual) */}
      <div className="absolute inset-0 p-2">
        {/* SVG (glow) */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none z-10"
          viewBox={`0 0 ${viewW} ${viewH}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ opacity: activePositions.length > 1 ? 1 : 0 }}
        >
          <defs>
            {/* gradiente em user space para não colapsar em linhas puras */}
            <linearGradient
              id="connectionGradient"
              gradientUnits="userSpaceOnUse"
              x1="0"
              y1="0"
              x2={viewW}
              y2="0"
            >
              {selectionError ? (
                // Gradiente vermelho para erro
                <>
                  <stop offset="0%" stopColor="#fca5a5" stopOpacity="0.9" />
                  <stop offset="50%" stopColor="#ef4444" stopOpacity="1" />
                  <stop offset="100%" stopColor="#dc2626" stopOpacity="0.9" />
                </>
              ) : selectionSuccess ? (
                // Gradiente verde para sucesso
                <>
                  <stop offset="0%" stopColor="#86efac" stopOpacity="0.9" />
                  <stop offset="50%" stopColor="#22c55e" stopOpacity="1" />
                  <stop offset="100%" stopColor="#16a34a" stopOpacity="0.9" />
                </>
              ) : (
                // Gradiente azul normal
                <>
                  <stop offset="0%" stopColor="#60a5fa" stopOpacity="0.9" />
                  <stop offset="50%" stopColor="#3b82f6" stopOpacity="1" />
                  <stop offset="100%" stopColor="#1d4ed8" stopOpacity="0.9" />
                </>
              )}
            </linearGradient>

            {/* filtro em user space e caixa ampla (funciona em H/V puros) */}
            <filter
              id="glow"
              filterUnits="userSpaceOnUse"
              x={-viewW}
              y={-viewH}
              width={viewW * 3}
              height={viewH * 3}
            >
              <feGaussianBlur
                stdDeviation={glowBlurDeviation}
                result="coloredBlur"
              />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* contorno neutro sobre o traço (reforço além da cor) */}
          <path
            d={generateConnectionPath(activePositions)}
            stroke={outlineStroke}
            strokeWidth={strokeW * 1.16}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity="0.5"
            vectorEffect="non-scaling-stroke"
          />

          {/* camada base branca */}
          <path
            d={generateConnectionPath(activePositions)}
            stroke="#ffffff"
            strokeWidth={Math.max(1.5, strokeW * 0.4)}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity="0.35"
            vectorEffect="non-scaling-stroke"
          />

          {/* faixa com gradiente + glow */}
          <path
            d={generateConnectionPath(activePositions)}
            stroke="url(#connectionGradient)"
            strokeWidth={strokeW}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            filter="url(#glow)"
            opacity="0.85"
            vectorEffect="non-scaling-stroke"
          />
        </svg>

        {/* GRID */}
        <div
          className="absolute inset-0 grid gap-1 z-10 sm:gap-1.5"
          style={{
            gridTemplateColumns: `repeat(${cols}, 1fr)`,
            gridTemplateRows: `repeat(${rows}, 1fr)`
          }}
        >
          {board.flat().map((letter, i) => {
            const r = Math.floor(i / cols)
            const c = i % cols
            const isSquareActive = isActive(r, c)
            const isSquareFound = isFound(r, c)
            const isSquareCompleted = isCompleted(r, c)

            return (
              <Square
                key={`${r}-${c}`}
                letter={letter}
                isActive={isSquareActive}
                isFound={isSquareFound}
                isCompleted={isSquareCompleted}
                onClick={onSquareClick}
                onEnter={onSquareEnter}
                selectionError={selectionError}
                selectionSuccess={selectionSuccess}
              />
            )
          })}
        </div>

        {/* CAMADA DE GESTO (captura mouse/touch/pen) */}
        <div
          ref={overlayRef}
          className="
            absolute inset-0 z-40
            pointer-events-auto
            touch-none select-none
            [overscroll-behavior:contain]
            [webkitTapHighlightColor:transparent]
            [user-select:none] [webkit-user-select:none] cursor-pointer
          "
          aria-hidden
        />
      </div>
    </div>
  )
},
arePropsEqual)

Board.displayName = 'Board'
